import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemButton,
    Divider,
    Collapse,
    IconButton,
    Tooltip,
    Alert
} from '@mui/material';
import {
    PlayArrow as StartIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Security as SecurityIcon,
    Schedule as TimeIcon,
    Bolt as DirectIcon,
    AccountTree as WorkflowIcon,
    Launch as LaunchIcon,
    Add as AddIcon
} from '@mui/icons-material';
import { WORKFLOW_TEMPLATES, WorkflowTemplate } from '../../utils/workflowTemplates';
import logger from '../../utils/logger';

interface WorkflowShortcutsProps {
    onStartWorkflow: (template: WorkflowTemplate) => void;
    onCreateWorkflowFromCurrent?: () => void;
    currentContext?: {
        contract?: string;
        functionName?: string;
        functionData?: string;
        description?: string;
    };
    compact?: boolean;
}

const WorkflowShortcuts: React.FC<WorkflowShortcutsProps> = ({
    onStartWorkflow,
    onCreateWorkflowFromCurrent,
    currentContext,
    compact = false
}) => {
    const [expanded, setExpanded] = useState(!compact);

    // Get quick access workflows (most common patterns)
    const quickWorkflows = WORKFLOW_TEMPLATES.filter(template =>
        ['memer_to_timelock_to_multisig', 'direct_multisig', 'timelock_to_multisig'].includes(template.id)
    );

    // Get context-aware suggestions
    const getContextSuggestions = () => {
        if (!currentContext?.contract) return [];

        const suggestions = [];

        // If we have function data, suggest workflows that can use it
        if (currentContext.functionData) {
            suggestions.push({
                template: WORKFLOW_TEMPLATES.find(t => t.id === 'memer_to_timelock_to_multisig'),
                reason: 'Use your current function in a governance workflow'
            });

            suggestions.push({
                template: WORKFLOW_TEMPLATES.find(t => t.id === 'direct_multisig'),
                reason: 'Execute your function directly via MultiSig'
            });
        }

        return suggestions.filter(s => s.template);
    };

    const contextSuggestions = getContextSuggestions();

    const handleStartWorkflow = (template: WorkflowTemplate) => {
        logger.log('🚀 Starting workflow from shortcuts:', template.name);

        // If we have current context, we could pre-populate the workflow
        if (currentContext?.functionData) {
            logger.log('📋 Pre-populating workflow with current context:', currentContext);
        }

        onStartWorkflow(template);
    };

    const getDifficultyColor = (difficulty: string) => {
        switch (difficulty) {
            case 'beginner': return 'success';
            case 'intermediate': return 'warning';
            case 'advanced': return 'error';
            default: return 'default';
        }
    };

    const getWorkflowIcon = (templateId: string) => {
        switch (templateId) {
            case 'memer_to_timelock_to_multisig':
                return <SecurityIcon color="primary" />;
            case 'direct_multisig':
                return <DirectIcon color="success" />;
            case 'timelock_to_multisig':
                return <TimeIcon color="warning" />;
            default:
                return <WorkflowIcon color="action" />;
        }
    };

    if (compact) {
        return (
            <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <WorkflowIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                        Workflow Shortcuts
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() => setExpanded(!expanded)}
                    >
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>

                <Collapse in={expanded}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {quickWorkflows.map((template) => (
                            <Button
                                key={template.id}
                                size="small"
                                variant="outlined"
                                startIcon={getWorkflowIcon(template.id)}
                                onClick={() => handleStartWorkflow(template)}
                                sx={{ fontSize: '0.75rem' }}
                            >
                                {template.name.split(' ')[0]}
                            </Button>
                        ))}
                    </Box>

                    {contextSuggestions.length > 0 && (
                        <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                            <Typography variant="caption">
                                💡 {contextSuggestions[0].reason}
                            </Typography>
                        </Alert>
                    )}
                </Collapse>
            </Paper>
        );
    }

    return (
        <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WorkflowIcon sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    Workflow Shortcuts
                </Typography>
                <Chip
                    label={`${WORKFLOW_TEMPLATES.length} templates`}
                    size="small"
                    variant="outlined"
                />
            </Box>

            {/* Context-aware suggestions */}
            {contextSuggestions.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
                        💡 Suggested for Current Context
                    </Typography>
                    <List dense>
                        {contextSuggestions.map((suggestion, index) => (
                            <ListItem key={index} disablePadding>
                                <ListItemButton
                                    onClick={() => handleStartWorkflow(suggestion.template!)}
                                    sx={{ borderRadius: 1, mb: 0.5 }}
                                >
                                    <ListItemIcon>
                                        {getWorkflowIcon(suggestion.template!.id)}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={suggestion.template!.name}
                                        secondary={suggestion.reason}
                                        primaryTypographyProps={{ variant: 'body2' }}
                                        secondaryTypographyProps={{ variant: 'caption' }}
                                    />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Chip
                                            label={suggestion.template!.difficulty}
                                            size="small"
                                            color={getDifficultyColor(suggestion.template!.difficulty) as any}
                                            variant="outlined"
                                        />
                                        <LaunchIcon fontSize="small" />
                                    </Box>
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                    <Divider sx={{ my: 2 }} />
                </Box>
            )}

            {/* Quick access workflows */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                🚀 Quick Start
            </Typography>
            <List dense>
                {quickWorkflows.map((template) => (
                    <ListItem key={template.id} disablePadding>
                        <ListItemButton
                            onClick={() => handleStartWorkflow(template)}
                            sx={{ borderRadius: 1, mb: 0.5 }}
                        >
                            <ListItemIcon>
                                {getWorkflowIcon(template.id)}
                            </ListItemIcon>
                            <ListItemText
                                primary={template.name}
                                secondary={`${template.steps.length} steps • ~${template.estimatedTime} min`}
                                primaryTypographyProps={{ variant: 'body2' }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                    label={template.difficulty}
                                    size="small"
                                    color={getDifficultyColor(template.difficulty) as any}
                                    variant="outlined"
                                />
                                <StartIcon fontSize="small" />
                            </Box>
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>

            {/* Create workflow from current context */}
            {onCreateWorkflowFromCurrent && currentContext?.functionData && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={onCreateWorkflowFromCurrent}
                        sx={{ textTransform: 'none' }}
                    >
                        Save Current Function as Workflow Step
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}>
                        Create a reusable workflow template from your current function
                    </Typography>
                </Box>
            )}

            {/* View all workflows */}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button
                    fullWidth
                    variant="text"
                    endIcon={<LaunchIcon />}
                    onClick={() => {
                        // This would navigate to the workflows tab
                        logger.log('Navigate to workflows tab');
                    }}
                    sx={{ textTransform: 'none' }}
                >
                    View All Workflow Templates ({WORKFLOW_TEMPLATES.length})
                </Button>
            </Box>
        </Paper>
    );
};

export default WorkflowShortcuts;
